import { useState, useEffect, useCallback } from 'react';
import db, { type User } from '@/db';
import { ensureDefaultUsers } from '@/db/seed';
import { accessLevel, responsibleNames } from '@/auth/access';
import { supabaseConfigured } from '@/services/supabaseClient';
import { useCloudAuth } from './useCloudAuth';

interface AuthState { user: User | null; originalUser: User | null; isImpersonating: boolean; isLoading: boolean }
const STORAGE_KEY = 'zsb_auth_user';
const SIMULATION_KEY = 'zsb_simulated_user_id';
const AUTH_EVENT = 'zsb-auth-change';
const notifyAuth = () => window.dispatchEvent(new Event(AUTH_EVENT));

async function hashPassword(password: string): Promise<string> {
  const data = new TextEncoder().encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function getStoredUser(): User | null {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
}

function setStoredUser(user: User | null) {
  if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  else localStorage.removeItem(STORAGE_KEY);
  notifyAuth();
}

async function resolveSession(): Promise<Omit<AuthState, 'isLoading'>> {
  const empty = { user: null, originalUser: null, isImpersonating: false };
  const stored = getStoredUser();
  if (!stored) return empty;
  const original = await db.users.get(stored.id);
  if (!original || original.active === false) return empty;
  let effective = original;
  let isImpersonating = false;
  const targetId = sessionStorage.getItem(SIMULATION_KEY);
  if (targetId && accessLevel(original) === 'admin' && !original.forcePasswordChange) {
    const target = await db.users.get(Number(targetId));
    if (target && target.active !== false && accessLevel(target) !== 'admin') {
      effective = { ...target, forcePasswordChange: false };
      isImpersonating = true;
    } else sessionStorage.removeItem(SIMULATION_KEY);
  } else if (targetId) sessionStorage.removeItem(SIMULATION_KEY);
  const members = accessLevel(effective) === 'manager'
    ? await db.users.filter(member => member.managerEmail?.toLowerCase() === effective.email.toLowerCase() && accessLevel(member) === 'seller').toArray()
    : [];
  return {
    user: { ...effective, teamResponsibleNames: members.flatMap(responsibleNames), teamEmails: members.map(m => m.email.toLowerCase()), teamProductCodes: [...(effective.productCodes || []), ...members.flatMap(m => m.productCodes || [])] },
    originalUser: isImpersonating ? original : null,
    isImpersonating,
  };
}

function useLocalAuth() {
  const [auth, setAuth] = useState<AuthState>({ user: null, originalUser: null, isImpersonating: false, isLoading: true });

  useEffect(() => {
    let alive = true;
    let generation = 0;
    const refresh = async () => {
      const current = ++generation;
      try {
        const session = await resolveSession();
        if (alive && generation === current) setAuth({ ...session, isLoading: false });
      } catch {
        if (alive && generation === current) setAuth({ user: null, originalUser: null, isImpersonating: false, isLoading: false });
      }
    };
    void refresh();
    window.addEventListener(AUTH_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => { alive = false; window.removeEventListener(AUTH_EVENT, refresh); window.removeEventListener('storage', refresh); };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    try {
      await ensureDefaultUsers();
      const user = await db.users.where('email').equals(email.trim().toLowerCase()).first();
      if (!user) return 'Usuário não encontrado';
      if (user.active === false) return 'Este usuário está oculto e sem acesso. Procure seu gerente.';
      if (user.passwordHash !== await hashPassword(password)) return 'Senha incorreta';
      sessionStorage.removeItem(SIMULATION_KEY);
      setStoredUser(user);
      return null;
    } catch { return 'Não foi possível acessar. Tente novamente.'; }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(SIMULATION_KEY);
    setStoredUser(null);
  }, []);

  const accessAs = useCallback(async (targetId: number): Promise<string | null> => {
    const session = await resolveSession();
    if (!session.user || session.isImpersonating || accessLevel(session.user) !== 'admin' || session.user.forcePasswordChange) return 'Somente o administrador pode simular um acesso.';
    const target = await db.users.get(targetId);
    if (!target || target.active === false || accessLevel(target) === 'admin') return 'Selecione um gerente ou vendedor ativo.';
    sessionStorage.setItem(SIMULATION_KEY, String(targetId));
    notifyAuth();
    return null;
  }, []);

  const stopAccessAs = useCallback(() => {
    sessionStorage.removeItem(SIMULATION_KEY);
    notifyAuth();
  }, []);

  const changePassword = useCallback(async (password: string): Promise<string | null> => {
    const session = await resolveSession();
    if (!session.user) return 'Usuário não autenticado';
    if (session.isImpersonating) return 'Encerre a simulação. A senha pessoal só pode ser trocada pelo próprio usuário.';
    if (password.length < 8) return 'A nova senha deve ter pelo menos 8 caracteres.';
    try {
      const passwordHash = await hashPassword(password);
      if (passwordHash === session.user.passwordHash) return 'A nova senha deve ser diferente da senha temporária ou atual.';
      await db.users.update(session.user.id, { passwordHash, forcePasswordChange: false });
      setStoredUser(await db.users.get(session.user.id) || null);
      return null;
    } catch { return 'Não foi possível alterar a senha. Tente novamente.'; }
  }, []);

  const changePasswordWithCurrent = useCallback(async (currentPassword: string, newPassword: string): Promise<string | null> => {
    const session = await resolveSession();
    if (!session.user) return 'Usuário não autenticado';
    if (session.isImpersonating) return 'Troca de senha indisponível na simulação.';
    if (session.user.passwordHash !== await hashPassword(currentPassword)) return 'A senha atual está incorreta.';
    return changePassword(newPassword);
  }, [changePassword]);

  return { ...auth, login, logout, changePassword, changePasswordWithCurrent, accessAs, stopAccessAs };
}

export { hashPassword };
export const useAuth = supabaseConfigured ? useCloudAuth : useLocalAuth;
